import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { createProject as createProjectAPI } from "../api/projects/projects";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import ChatInput from "./ChatInput";
import { Sun, Moon, Folder } from "lucide-react";
import { useTheme } from "next-themes";
import { useProjects } from "../hooks/useProjects";
import { ProjectThumbnail } from "./ProjectThumbnail";
import { useWebSocket } from "../contexts/Websocket/WebSocketProvider";
import {
  startExecution,
  resetExecution,
  isExecutionActive,
} from "../../redux/reducers/Execution";
import { buildPingMessage } from "../utils/buildPingMessage";
import { reducerType } from "../../redux/reducers";
import { AppDispatch } from "../../redux/store";
import { extractSfdcId } from "@amzn/afxtaco-utilities";
import { recordEvent } from "../monitoring";
import { useFeatureFlags } from "../utils/FeatureFlagDetection";
import Footer from "./Footer";
import { HelpPanel } from "./helpPanel";
import {
  CapabilitiesAccordion,
  OptionalContextFields,
  ContextFields,
  persistCustomerContext,
  optimisticCustomerProject,
} from "./homepage";
import { addProject } from "../../redux/reducers/Projects";

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { theme, setTheme } = useTheme();
  const [inputValue, setInputValue] = useState("");
  const ws = useWebSocket();
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);
  // Only gates whether the context fields show format errors. The IDs are still
  // sent and persisted either way — see the send handler below.
  const { isOverviewPageEnabled } = useFeatureFlags(["overviewPage"]);
  const [contextFields, setContextFields] = useState<ContextFields>({
    accountId: "",
    opportunityId: "",
  });

  // Holds a project created eagerly for an attachment upload so onMessageSend
  // can reuse its id and target it with NEW_SESSION (project already exists).
  const pendingProjectIdRef = useRef<string | null>(null);

  // Eager-creates the project server-side so attachments can upload under it
  // before the message is sent. Idempotent: a failed-then-retried send reuses
  // the same project rather than leaking a new empty one. Only invoked by
  // ChatInput when fileUpload is enabled and attachments are present.
  const resolveUploadProjectId = useCallback(async () => {
    if (pendingProjectIdRef.current) return pendingProjectIdRef.current;
    const projectId = uuidv4();
    // Claim the slot synchronously (before the await) so a concurrent call
    // reuses this id instead of creating a second orphan project.
    pendingProjectIdRef.current = projectId;
    try {
      await createProjectAPI(projectId, {}); // owner stamped from auth token
    } catch (err) {
      pendingProjectIdRef.current = null; // release so a retry can start fresh
      throw err;
    }
    return projectId;
  }, []);

  // Drop any pending id on unmount so it can't leak into a later unrelated send.
  useEffect(() => {
    return () => {
      pendingProjectIdRef.current = null;
    };
  }, []);

  // Check if an execution is already active (e.g. rapid double-click)
  const executionState = useSelector((state: reducerType) => state.Execution);
  const executionActive = isExecutionActive(executionState.status);

  // Get projects from Redux store (already loaded by DataProvider)
  const {
    projects: reduxProjects,
    createProject,
    updateProject,
  } = useProjects();

  // Auto-focus the chat input when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[placeholder="Describe your new project"]',
      );
      if (textarea) {
        (textarea as HTMLTextAreaElement).focus();
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // Get the 4 most recent projects directly from Redux
  const recentProjects = React.useMemo(
    () => reduxProjects.slice(0, 4),
    [reduxProjects],
  );

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Top Navigation */}
      <div className="h-14 border-b border-border bg-background flex items-center px-4 gap-4">
        {/* Left: Logo and description */}
        <div
          className="flex items-center gap-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => (window.location.href = "/")}
        >
          <img
            src="/logo.png"
            alt="TACO Logo"
            className="w-5 h-5 object-contain"
          />
          <span className="font-semibold text-foreground">TACO</span>
          <div className="w-px h-4 bg-gray-300 mx-1"></div>
          <span className="text-sm text-muted-foreground">
            Technical Architecture Composer
          </span>
        </div>

        {/* Right: My Projects, Theme, User */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => navigate("/projects")}
          >
            <Folder className="h-4 w-4" />
            My Projects
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
        </div>
      </div>

      {/* Main layout: content + help panel in a flex row */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto flex flex-col">
          {/* Main Content */}
          <div className="flex flex-col px-6 py-4 lg:py-2">
            {/* Spacer */}
            <div className="flex-1 lg:flex-[0.3] 2xl:h-16" />

            {/* Logo and Title */}
            <div className="text-center mb-4 mt-4 pt-[60px]">
              <div className="flex items-center justify-center gap-4 mb-2">
                <img
                  src="/logo.png"
                  alt="TACO Logo"
                  className="w-12 h-12 object-contain"
                />
                <h1 className="text-4xl font-bold">TACO</h1>
              </div>
              <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
                An AI-powered assistant to rapidly design and build
                comprehensive AWS architecture solution packages
              </p>

              {/* Capabilities Accordion */}
              <CapabilitiesAccordion />
            </div>

            {/* Form Card: ChatInput + Optional Context Fields */}
            <div className="w-full max-w-4xl mx-auto mb-2 [&_.border-t]:border-t-0">
              <ChatInput
                defaultMessage={inputValue}
                disabled={executionActive}
                resolveUploadProjectId={resolveUploadProjectId}
                onHelpClick={() => setIsHelpPanelOpen(true)}
                slotBelowTextarea={
                  <OptionalContextFields
                    fields={contextFields}
                    onChange={setContextFields}
                    showValidation={isOverviewPageEnabled}
                  />
                }
                onMessageSend={async (message, _features, attachments = []) => {
                  // Guard against double-submit (rapid double-click / Enter)
                  if (executionActive) return;

                  // Normalize once — a field can hold a bare ID or a pasted
                  // Salesforce Lightning URL, and OptionalContextFields
                  // already normalizes on change, but re-extracting here is
                  // a cheap no-op for an already-clean ID and guards against
                  // contextFields ever being set some other way. Reused below
                  // for the structured customerContext so both agree on the
                  // same value.
                  const accountId = extractSfdcId(contextFields.accountId);
                  const opportunityId = extractSfdcId(
                    contextFields.opportunityId,
                  );

                  // Append non-empty optional context fields to the prompt
                  let finalMessage = message;
                  const contextParts: string[] = [];
                  if (opportunityId) {
                    contextParts.push(`- Opportunity ID: ${opportunityId}`);
                  }
                  if (accountId) {
                    contextParts.push(`- Account ID: ${accountId}`);
                  }
                  if (contextParts.length > 0) {
                    finalMessage = `${message}\n\n---\nAdditional context:\n${contextParts.join("\n")}`;
                  }

                  // Reset any lingering execution state from a previous project.
                  dispatch(resetExecution());

                  // Reuse the project eager-created for an attachment upload, if any;
                  // otherwise mint a fresh id for the backend to create lazily.
                  const eagerProjectId = pendingProjectIdRef.current;
                  pendingProjectIdRef.current = null;
                  const projectId = eagerProjectId ?? uuidv4();
                  const sessionId = uuidv4();
                  const clientRequestId = uuidv4();

                  dispatch(
                    startExecution({
                      projectId,
                      sessionId,
                      clientRequestId,
                      isNewProject: true,
                    }),
                  );

                  const attachedFiles = attachments.map((a) => ({
                    name: a.name,
                    s3Key: a.s3Key,
                  }));

                  // Structured copy of the IDs for the agent. Sent alongside the
                  // prompt text above so this first turn is grounded even before
                  // the project row exists.
                  const customerContext =
                    accountId || opportunityId
                      ? {
                          ...(accountId ? { accountIds: [accountId] } : {}),
                          ...(opportunityId
                            ? { opportunityIds: [opportunityId] }
                            : {}),
                        }
                      : undefined;

                  const pingMessage = await buildPingMessage({
                    projectId,
                    sessionId,
                    clientRequestId,
                    prompt: finalMessage,
                    initAction: eagerProjectId ? "NEW_SESSION" : "NEW_PROJECT",
                    isProjectNew: true,
                    ...(attachedFiles.length > 0 ? { attachedFiles } : {}),
                    ...(customerContext ? { customerContext } : {}),
                  });
                  ws?.sendMessage(pingMessage);

                  // Persist the IDs onto the project so they show in the
                  // Overview's Customer box and ride every later turn. Done
                  // after the ping, not before: the payload already grounded
                  // this turn, so a slow or failed write must not delay the
                  // send — it only costs the Customer box until the next write.
                  if (customerContext) {
                    // Seed the store first, synchronously. The project view does
                    // not fetch during the new-project flow, so the Customer box
                    // would otherwise have nothing to read until the turn
                    // completed. The create call below replaces this record.
                    dispatch(
                      addProject(
                        optimisticCustomerProject(
                          projectId,
                          { accountId, opportunityId },
                          new Date().toISOString(),
                        ),
                      ),
                    );

                    persistCustomerContext(projectId, {
                      accountId,
                      opportunityId,
                      alreadyExists: Boolean(eagerProjectId),
                      createProject,
                      updateProject,
                    }).catch((err) =>
                      console.error("Failed to persist customer context:", err),
                    );
                  }

                  recordEvent("taco.project.created", {
                    projectId,
                    hasAttachments: attachments.length > 0,
                    hasContext: contextParts.length > 0,
                  });

                  navigate(`/project/${projectId}`, {
                    state: {
                      description: finalMessage,
                      sessionId,
                      isNewProject: true,
                      ...(attachments.length > 0 ? { attachments } : {}),
                    },
                  });
                  setInputValue("");
                  setContextFields({
                    accountId: "",
                    opportunityId: "",
                  });
                }}
              />
            </div>

            {/* Spacer */}
            <div className="flex-1 lg:flex-[0.4] xl:flex-[0.15] 2xl:h-8" />
          </div>

          {/* Recent Projects */}
          <div className="bg-background">
            <div className="max-w-6xl mx-auto px-6 py-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Recent projects</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={() => navigate("/projects")}
                >
                  View all projects
                </Button>
              </div>

              {/* Project Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentProjects.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    No projects yet. Create your first project above!
                  </div>
                ) : (
                  recentProjects.map((project, index) => (
                    <Card
                      key={index}
                      className="hover:shadow-md transition-shadow overflow-hidden group"
                    >
                      <div
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(`/project/${project.projectId}`)
                        }
                      >
                        <div className="relative">
                          <ProjectThumbnail
                            projectId={project.projectId}
                            projectName={
                              project.projectName || "Untitled Project"
                            }
                            index={index}
                            className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>

                        <div className="p-4">
                          <h3 className="font-medium text-foreground hover:text-blue-600 line-clamp-1 mb-2">
                            {project.projectName || "Untitled Project"}
                          </h3>

                          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                            {project.projectDescription}
                          </p>
                          <div className="flex items-center justify-end text-xs text-muted-foreground">
                            <span>
                              {project.updatedAt
                                ? new Date(
                                    project.updatedAt,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "Unknown"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </div>

          <Footer className="mt-auto" />
        </div>

        {/* Help Panel — pushes content when open */}
        <div
          className="flex-shrink-0 overflow-hidden transition-all duration-300"
          style={{ width: isHelpPanelOpen ? 600 : 0 }}
        >
          <HelpPanel
            isOpen={isHelpPanelOpen}
            onClose={() => setIsHelpPanelOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
